import React, { createContext, useContext, useCallback } from 'react';
import { useBridgeForm, useBuyBridgeNomForm, useSellBridgeNomForm } from '@onomy/react-hub';
import { AsyncStatus, useAsyncStatuses } from '@onomy/react-utils';
import { OnomyHubConstants } from '@onomy/hub';

export function useBondingCurveExchange() {
  const {
    loadingStatus: buyLoadingStatus,
    txStatus: buyTxStatus,

    ethBalance,
    ethBidAmount,

    gas: buyGas,

    bNomAskAmount: bNomQuote,

    ethBidAmountFixed,

    canSubmit: buyCanSubmit,
    formErrors: buyFormErrors,

    setEthBidAmountFixed,

    slippagePoints: buySlippagePoints,

    txDetails: buyTxDetails,

    setSlippagePoints: setBuySlippagePoints,
    doPurchase,
    reset: buyReset,
  } = useBuyBridgeNomForm();

  const {
    loadingStatus: sellLoadingStatus,
    txStatus: sellTxStatus,
    approveTxStatus: approveSellTxStatus,

    bNomBalance,
    bNomAskAmount,
    bNomApproveAmount,

    gas: sellGas,

    ethBidAmount: ethQuote,

    isMaxAmount: sellIsMaxAmount,
    isMaxApproveAmount: approveSellIsMaxAmount,
    bNomSellAllowance,
    extraAllowanceNeeded,

    bNomAskAmountFixed,
    bNomApproveFixed,

    canSubmit: sellCanSubmit,
    formErrors: sellFormErrors,

    setBNomAskAmountFixed,
    setBNomApproveFixed,

    toggleMaxAmount: toggleSellMaxAmount,
    toggleMaxApproveAmount: toggleApproveSellMaxAmount,

    slippagePoints: sellSlippagePoints,

    txDetails: sellTxDetails,
    approveTxDetails: approveSellTxDetails,

    setSlippagePoints: setSellSlippagePoints,
    doSell,
    doApprove: doApproveForSale,
    reset: sellReset,
    resetApprove: approveSellReset,
  } = useSellBridgeNomForm();

  const {
    loadingStatus: bridgeLoadingStatus,
    txStatus: bridgeTxStatus,
    bridgeStatus,

    approveTxStatus: bridgeApproveTxStatus,
    txDetails: bridgeTxDetails,
    approveTxDetails: bridgeApproveTxDetails,

    toBalance: nomBalance,
    bridgeAllowance,
    bridgeAmount,
    bridgeApproveAmount,
    extraAllowanceNeeded: bridgeExtraAllowanceNeeded,

    bridgeAmountFixed,
    bridgeApproveFixed,

    txGas: bridgeGas,
    approveGas: bridgeApproveGas,

    setBridgeAmountFixed,
    setBridgeApproveFixed,

    txFormErrors: bridgeFormErrors,
    // TODO: approval form errors,
    canSubmitTx: bridgeCanSubmit,

    toggleMaxAmount: toggleBridgeMaxAmount,
    toggleMaxApproveAmount: toggleApproveBridgeMaxAmount,

    reset: bridgeReset,
    resetApprove: bridgeApproveReset,

    doApprove: doBridgeApprove,
    doBridge,
  } = useBridgeForm({
    fromAsset: OnomyHubConstants.BNOM_ASSET,
    toAsset: OnomyHubConstants.NOM_ASSET,
  });

  const loadingStatus = useAsyncStatuses([buyLoadingStatus, sellLoadingStatus]);

  const resetAll = useCallback(() => {
    buyReset();
    sellReset();
    bridgeReset();
  }, [buyReset, sellReset, bridgeReset]);

  return {
    loadingStatus,

    buyLoadingStatus,
    buyTxStatus,
    ethBalance,
    ethBidAmount,
    buyGas,
    bNomQuote,
    ethBidAmountFixed,
    buySlippagePoints,
    buyTxDetails,
    buyCanSubmit,
    buyFormErrors,
    setEthBidAmountFixed,
    setBuySlippagePoints,
    doPurchase,
    buyReset,

    sellLoadingStatus,
    sellTxStatus,
    approveSellTxStatus,
    bNomBalance,
    bNomAskAmount,
    bNomApproveAmount,
    sellGas,
    ethQuote,
    sellIsMaxAmount,
    approveSellIsMaxAmount,
    bNomSellAllowance,
    extraAllowanceNeeded,
    bNomAskAmountFixed,
    bNomApproveFixed,
    sellSlippagePoints,
    sellTxDetails,
    sellCanSubmit,
    sellFormErrors,
    approveSellTxDetails,
    setBNomAskAmountFixed,
    setBNomApproveFixed,
    setSellSlippagePoints,
    toggleSellMaxAmount,
    toggleApproveSellMaxAmount,
    doSell,
    doApproveForSale,
    sellReset,
    approveSellReset,

    bridgeStatus,
    bridgeLoadingStatus,
    bridgeTxStatus,
    bridgeApproveTxStatus,
    bridgeTxDetails,
    bridgeApproveTxDetails,
    nomBalance,
    bridgeAllowance,
    bridgeAmount,
    bridgeApproveAmount,
    bridgeExtraAllowanceNeeded,
    bridgeAmountFixed,
    bridgeApproveFixed,
    bridgeGas,
    bridgeApproveGas,
    setBridgeAmountFixed,
    setBridgeApproveFixed,
    bridgeFormErrors,
    bridgeCanSubmit,
    toggleBridgeMaxAmount,
    toggleApproveBridgeMaxAmount,
    bridgeReset,
    bridgeApproveReset,
    doBridgeApprove,
    doBridge,

    resetAll,
  };
}
type ExchangeContextType = ReturnType<typeof useBondingCurveExchange>;

const LOADED_STATUS: AsyncStatus = {
  pending: false,
  error: null,
  finished: true,
};

const INIT_STATUS: AsyncStatus = {
  pending: false,
  error: null,
  finished: false,
};

const GAS_FORM = {
  loadingStatus: LOADED_STATUS,
  detailedOptionAmountsStatus: LOADED_STATUS,
  gasAmount: OnomyHubConstants.UNKNOWN_BALANCE,
  gasPrice: '0',
  options: [],
  setGasPrice: () => {},
  gasEstimate: '0',
};

const UNKNOWN_ETH_BALANCE = {
  ...OnomyHubConstants.UNKNOWN_BALANCE,
  asset: OnomyHubConstants.ETH_ASSET,
};

const UNKNOWN_BNOM_BALANCE = {
  ...OnomyHubConstants.UNKNOWN_BALANCE,
  asset: OnomyHubConstants.BNOM_ASSET,
};

const UNKNOWN_NOM_BALANCE = {
  ...OnomyHubConstants.UNKNOWN_BALANCE,
  asset: OnomyHubConstants.NOM_ASSET,
};

export const EMPTY_EXCHANGE_CONTEXT: ExchangeContextType = {
  loadingStatus: LOADED_STATUS,

  buyLoadingStatus: LOADED_STATUS,
  buyTxStatus: INIT_STATUS,
  ethBalance: UNKNOWN_ETH_BALANCE,
  ethBidAmount: UNKNOWN_ETH_BALANCE,
  buyGas: GAS_FORM,
  bNomQuote: UNKNOWN_BNOM_BALANCE,
  ethBidAmountFixed: '',
  buySlippagePoints: 10000,
  buyTxDetails: null,
  buyCanSubmit: false,
  buyFormErrors: null,
  setEthBidAmountFixed: () => {},
  setBuySlippagePoints: () => {},
  doPurchase: () => Promise.resolve(),
  buyReset: () => {},

  sellLoadingStatus: LOADED_STATUS,
  sellTxStatus: INIT_STATUS,
  approveSellTxStatus: INIT_STATUS,
  bNomBalance: UNKNOWN_BNOM_BALANCE,
  bNomAskAmount: UNKNOWN_BNOM_BALANCE,
  bNomApproveAmount: UNKNOWN_BNOM_BALANCE,
  sellGas: GAS_FORM,
  ethQuote: UNKNOWN_ETH_BALANCE,
  sellIsMaxAmount: false,
  approveSellIsMaxAmount: false,
  bNomSellAllowance: UNKNOWN_BNOM_BALANCE,
  extraAllowanceNeeded: UNKNOWN_BNOM_BALANCE,
  bNomAskAmountFixed: '',
  bNomApproveFixed: '',
  sellSlippagePoints: 10000,
  sellTxDetails: null,
  sellCanSubmit: false,
  sellFormErrors: null,
  approveSellTxDetails: null,
  setBNomAskAmountFixed: () => {},
  setBNomApproveFixed: () => {},
  setSellSlippagePoints: () => {},
  toggleSellMaxAmount: () => {},
  toggleApproveSellMaxAmount: () => {},
  doSell: () => Promise.resolve(),
  doApproveForSale: () => Promise.resolve(),
  sellReset: () => {},
  approveSellReset: () => {},

  bridgeStatus: null,
  bridgeLoadingStatus: LOADED_STATUS,
  bridgeTxStatus: INIT_STATUS,
  bridgeApproveTxStatus: LOADED_STATUS,
  bridgeTxDetails: null,
  bridgeApproveTxDetails: null,
  nomBalance: UNKNOWN_NOM_BALANCE,
  bridgeAllowance: UNKNOWN_BNOM_BALANCE,
  bridgeAmount: UNKNOWN_BNOM_BALANCE,
  bridgeApproveAmount: UNKNOWN_BNOM_BALANCE,
  bridgeExtraAllowanceNeeded: UNKNOWN_BNOM_BALANCE,
  bridgeAmountFixed: '',
  bridgeApproveFixed: '',
  bridgeGas: GAS_FORM,
  bridgeApproveGas: GAS_FORM,
  setBridgeAmountFixed: () => {},
  setBridgeApproveFixed: () => {},
  bridgeFormErrors: null,
  bridgeCanSubmit: false,
  toggleBridgeMaxAmount: () => {},
  toggleApproveBridgeMaxAmount: () => {},
  bridgeReset: () => {},
  bridgeApproveReset: () => {},
  doBridgeApprove: () => Promise.resolve(),
  doBridge: () => Promise.resolve(),

  resetAll: () => {},
};

export const ExchangeContext = createContext<ExchangeContextType>(EMPTY_EXCHANGE_CONTEXT);
export const useExchange = () => useContext(ExchangeContext);

function ExchangeProvider({ children }: { children: React.ReactNode }) {
  const val = useBondingCurveExchange();
  return <ExchangeContext.Provider value={val}>{children}</ExchangeContext.Provider>;
}

export default ExchangeProvider;
